import React, { useState } from "react";
import {
  Check,
  FileText,
  SearchCheck,
  ClipboardCheck,
  FileQuestion,
  Scale,
  Briefcase,
} from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";

const PricingSection = ({ onOpenModal, onTmSignUpTypeChange }) => {
  const [activeTab, setActiveTab] = useState("self-filing");

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const tmSignUpType = tab === "self-filing" ? 1 : 2;
    if (onTmSignUpTypeChange) {
      onTmSignUpTypeChange(tmSignUpType);
    }
  };

  const handleGetStarted = (plan) => {
    const tmSignUpType = activeTab === "self-filing" ? 1 : 2;
    if (onOpenModal) {
      onOpenModal(`Get Started - ${plan.name} Plan`, tmSignUpType, {
        title: plan.name,
        amount: plan.price,
      });
    }
  };

  // Self Filing with Assistance pricing data
  const selfFilingPlans = [
    {
      id: "basic",
      name: "Basic",
      price: 45,
      supporting: "+ USPTO fees",
      popular: false,
      paymentNotice:
        "Simple guided process with expert support at every step to ensure accuracy",
      features: [
        "Case Review",
        "Basic Trademark Search",
        "Trademark Monitoring Services (1 Month)",
        "Complete Paperwork",
      ],
    },
    {
      id: "deluxe",
      name: "Deluxe",
      price: 145,
      supporting: "+ USPTO fees",
      popular: true,
      paymentNotice:
        "Balanced assistance that helps you move forward with clear professional direction",
      features: [
        "Case Review",
        "Case Filing",
        "Basic Search",
        "State E-Filing",
        "Trademark Secured",
        "Direct Hit Search",
        "Dedicated Case Manager",
      ],
    },
    {
      id: "premium",
      name: "Premium",
      price: 299,
      supporting: "+ USPTO fees",
      popular: false,
      paymentNotice:
        "Advanced support with deeper planning, careful review, and ongoing clarity",
      features: [
        "Everything in Deluxe +",
        "Case Review (by a TM Expert)",
        "Basic Free Trademark Search",
        "Trademark Monitoring Services (3 Months)",
        "Trademark Secured",
        "Refusal Risk Meter",
        "Case filing service with complete paperwork",
      ],
    },
    {
      id: "elite",
      name: "Elite",
      price: 599,
      supporting: "+ USPTO fees",
      popular: true,
      paymentNotice:
        "Premium guided service with expert attention, leadership, and complete confidence",
      features: [
        "Case Review & Filing (U.S Registered IP Attorney)",
        "Assigned Case Filing Officer",
        "Private Registration",
        "Trademark Secured",
        "Federal Search & Clearance Report (All States)",
        "Federal E-Filing (USPTO)",
        "Cease & Desist Letter",
        "Same Day Filing",
        "Trademark Monitoring (1 Year)",
        "Direct Hit Search",
        "Dedicated Case Manager",
        "24/7 Secure Online Access",
        "100% Approval Guarantee",
      ],
    },
  ];

  // IP Attorney-Led Filing pricing data
  const attorneyLedPlans = [
    {
      id: "attorney-basic",
      name: "Basic",
      price: 795,
      supporting: "",
      popular: false,
      paymentNotice:
        "Reliable professional support for a smoother and more confident filing process",
      features: [
        "Attorney Consultation",
        "Comprehensive Search",
        "Application Drafting",
        "USPTO Filing",
        "Office Action Response",
        "Registration Certificate",
      ],
    },
    {
      id: "attorney-standard",
      name: "Standard",
      price: 895,
      supporting: "",
      popular: true,
      paymentNotice:
        "Guided step-by-step assistance with added care, clarity, and peace of mind",
      features: [
        "Attorney Consultation",
        "Comprehensive Search",
        "Application Drafting",
        "USPTO Filing",
        "Office Action Response",
        "Registration Certificate",
        "Trademark Monitoring (6 Months)",
      ],
    },
    {
      id: "attorney-premium",
      name: "Premium",
      price: 1145,
      supporting: "",
      popular: false,
      paymentNotice:
        "Organized service with expert review, careful preparation, and steady progress",
      features: [
        "Attorney Consultation",
        "Comprehensive Search",
        "Application Drafting",
        "USPTO Filing",
        "Office Action Response",
        "Registration Certificate",
        "Trademark Monitoring (1 Year)",
        "Cease & Desist Letter",
      ],
    },
    {
      id: "attorney-elite",
      name: "Elite",
      price: 1545,
      supporting: "",
      popular: true,
      paymentNotice:
        "Priority support with focused attention, quicker handling, and greater confidence",
      feeIncluded: true,
      features: [
        "Attorney Consultation",
        "Comprehensive Search",
        "Application Drafting",
        "USPTO Filing",
        "Office Action Response",
        "Registration Certificate",
        "Trademark Monitoring (2 Years)",
        "Cease & Desist Letter",
        "Same Day Filing",
        "Priority Support",
      ],
    },
  ];

  const ProcessCard = ({ icon: Icon, title, description }) => (
    <Card className="p-6 bg-white hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-[#FFB400]">
      <div className="w-14 h-14 bg-gradient-to-br from-[#FFB400] to-[#D72638] rounded-full flex items-center justify-center mb-4">
        <Icon className="h-7 w-7 text-white" />
      </div>
      <h3 className="text-xl font-bold text-[#2B0A12] mb-3">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </Card>
  );

  const PricingCard = ({ plan, onGetStarted }) => (
    <Card
      className={`relative overflow-hidden transition-all duration-300 hover:scale-105 bg-white ${
        plan.popular
          ? "border-4 border-[#D72638] shadow-2xl scale-105"
          : "border-2 border-gray-200 hover:border-[#FFB400] hover:shadow-xl"
      }`}
    >
      {plan.popular && (
        <div className="absolute top-0 right-0 bg-gradient-to-r from-[#D72638] to-[#FFB400] text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
          {plan.name === "Elite" ? "Flagship Service" : "Core Offering"}
        </div>
      )}
      <div className="p-6">
        <h3 className="text-2xl font-bold text-[#2B0A12] mb-4">{plan.name}</h3>
        <div className="mb-4">
          <span className="text-4xl font-bold text-[#D72638]">
            ${plan.price}
          </span>
          {plan.supporting.length > 0 && (
            <span className="text-xxl font-medium text-gray-500 ml-1">
              {plan.supporting}
            </span>
          )}
        </div>
        <div className="bg-gray-100 p-3 rounded-lg mb-4">
          <p className="text-[11px] text-gray-600 leading-relaxed">
            {plan.paymentNotice}
          </p>
        </div>
        <ul className="space-y-3 mb-6 h-[280px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-[#FFB400] scrollbar-track-gray-200">
          {plan.features.map((feature, i) => (
            <li key={i} className="flex items-start space-x-2">
              <Check className="h-5 w-5 text-[#FFB400] flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>
        <Button
          onClick={() => onGetStarted(plan)}
          className={`w-full font-semibold transition-all duration-300 ${
            plan.popular
              ? "bg-gradient-to-r from-[#D72638] to-[#FFB400] hover:from-[#c21f2f] hover:to-[#e5a200] text-white"
              : "bg-[#2B0A12] hover:bg-[#3D1C24] text-white"
          }`}
        >
          Get Started
        </Button>
        <p className="text-xs text-gray-500 text-center mt-3">
          {plan.feeIncluded
            ? "*(Govt. filing fee included)"
            : "*(Govt. filing fee not included)"}
        </p>
      </div>
    </Card>
  );

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Intro Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#2B0A12] mb-6">
            Brand Protection Made Simple
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Trademark Essential helps businesses secure their identity through
            reliable filing, monitoring, renewals, and complete trademark
            support. Whether your goal is to protect a company name, claim
            ownership of a logo, or safeguard a slogan, our team makes the
            process clear and manageable. We assist brands across all 50 U.S.
            states and international markets, helping keep your intellectual
            property protected wherever your business operates.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => handleTabChange("self-filing")}
              className={`px-6 py-3 rounded-md font-semibold transition-all duration-300 ${
                activeTab === "self-filing"
                  ? "bg-[#D72638] text-white shadow-lg"
                  : "text-gray-600 hover:text-[#2B0A12]"
              }`}
              aria-selected={activeTab === "self-filing"}
              role="tab"
            >
              Self Filing with Assistance
            </button>
            <button
              onClick={() => handleTabChange("attorney-led")}
              className={`px-6 py-3 rounded-md font-semibold transition-all duration-300 ${
                activeTab === "attorney-led"
                  ? "bg-[#D72638] text-white shadow-lg"
                  : "text-gray-600 hover:text-[#2B0A12]"
              }`}
              aria-selected={activeTab === "attorney-led"}
              role="tab"
            >
              IP Attorney-Led Filing
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          {activeTab === "self-filing" ? (
            <>
              {/* Process Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <ProcessCard
                  icon={FileText}
                  title="Application Preparation"
                  description="Trademark Essential reviews your filing information, identifies the correct trademark class, and prepares your application for submission through the USPTO TEAS system."
                />
                <ProcessCard
                  icon={SearchCheck}
                  title="Identity Review and Approval"
                  description="You complete ID.me verification, then check the attorney-prepared application in detail. Any required edits or changes can be requested before submission."
                />
                <ProcessCard
                  icon={ClipboardCheck}
                  title="USPTO Filing"
                  description="After approval, Trademark Essential submits your application to the USPTO, ensuring every detail is accurate, complete, and ready for official review."
                />
              </div>

              {/* Pricing Section Header */}
              <div className="text-center mb-12">
                <h3 className="text-3xl md:text-4xl font-bold text-[#2B0A12] mb-3">
                  Self-Filing with Expert Assistance and Complete Support Plan
                  for Easy Filing
                </h3>
              </div>

              {/* Pricing Cards */}
              <div className="flex flex-wrap justify-center gap-4 max-w-7xl mx-auto">
                {selfFilingPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className="w-full md:w-[calc(50%-8px)] lg:w-[calc(25%-12px)] xl:w-[270px]"
                  >
                    <PricingCard plan={plan} onGetStarted={handleGetStarted} />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Process Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <ProcessCard
                  icon={FileQuestion}
                  title="Information Collection"
                  description="We collect your trademark details, including name, logo (if applicable), and a brief description of your products/services."
                />
                <ProcessCard
                  icon={Scale}
                  title="Attorney Review & Application Drafting"
                  description="Our IP attorney reviews your case, determines the appropriate trademark class(es), and prepares a strong, USPTO-compliant application."
                />
                <ProcessCard
                  icon={Briefcase}
                  title="Filing & Case Management"
                  description="We file the application on your behalf and handle all procedural steps, including monitoring your application and managing any USPTO correspondence."
                />
              </div>

              {/* Pricing Section Header */}
              <div className="text-center mb-12">
                <h3 className="text-3xl md:text-4xl font-bold text-[#2B0A12] mb-3">
                  Attorney Led Filing with Complete End-to-End Service Plan
                </h3>
              </div>

              {/* Pricing Cards */}
              <div className="flex flex-wrap justify-center gap-4 max-w-7xl mx-auto">
                {attorneyLedPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className="w-full md:w-[calc(50%-8px)] lg:w-[calc(25%-12px)] xl:w-[270px]"
                  >
                    <PricingCard plan={plan} onGetStarted={handleGetStarted} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
